import { useState, useEffect } from "react";
import { submitKangstudyForm, formDataToRecord } from "@/lib/submit-form";
import { useParams, Link } from "react-router-dom";
import { lectures, difficultyConfig } from "@/mocks/lectures";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import RegistrationModal from "@/pages/courses/components/RegistrationModal";
import PreviewModal from "../components/PreviewModal";
import { motion } from "motion/react";

export default function LectureDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [lecture, setLecture] = useState<(typeof lectures)[0] | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLectureViewer, setShowLectureViewer] = useState(false);

  useEffect(() => {
    const found = lectures.find((l) => l.id === id);
    if (found) {
      setLecture(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
    window.scrollTo(0, 0);
  }, [id]);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    setIsSubmitting(true);

    try {
      const { ok, error } = await submitKangstudyForm(
        "lecture_inquiry",
        formDataToRecord(formData),
      );

      if (ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormError(error || "오류가 발생했습니다. 다시 시도해주세요.");
        setFormStatus("error");
      }
    } catch {
      setFormError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      setFormStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-[#C4956A]/10 flex items-center justify-center">
              <i className="ri-video-off-line text-3xl text-[#C4956A]"></i>
            </div>
            <h1 className="text-xl font-bold text-[#1C1917] mb-2">강의를 찾을 수 없습니다</h1>
            <p className="text-sm text-[#8C8780] mb-6">존재하지 않는 강의이거나 삭제된 강의입니다</p>
            <Link to="/lectures" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#3A3530] transition-all">
              <i className="ri-arrow-left-line"></i>
              강의 목록으로 돌아가기
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!lecture) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1080px] mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-[#8C8780] mb-6">
            <Link to="/lectures" className="hover:text-[#C4956A] transition-colors">
              동영상 강의
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#5C5650] truncate max-w-[300px]">{lecture.title}</span>
          </div>

          {/* Video Viewer Area */}
          {showLectureViewer ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-video rounded-2xl overflow-hidden bg-black mb-8 flex items-center justify-center"
            >
              <img loading="lazy" decoding="async"
                src={lecture.thumbnail}
                alt={lecture.title}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
              />
              <div className="relative z-10 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#C4956A] flex items-center justify-center">
                  <i className="ri-play-fill text-3xl text-white ml-0.5"></i>
                </div>
                <p className="text-white font-bold text-lg mb-1">지금 재생 중</p>
                <p className="text-white/60 text-sm">{lecture.title}</p>
              </div>
              <button
                onClick={() => setShowLectureViewer(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer z-20"
                aria-label="닫기"
              >
                <i className="ri-close-line text-white text-lg"></i>
              </button>
            </motion.div>
          ) : (
            <div className="relative aspect-video rounded-2xl overflow-hidden mb-8 group">
              <img loading="lazy" decoding="async"
                src={lecture.thumbnail}
                alt={lecture.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-all flex items-center justify-center">
                <button
                  onClick={() => setShowLectureViewer(true)}
                  className="w-20 h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all hover:scale-105 shadow-2xl cursor-pointer"
                >
                  <i className="ri-play-fill text-3xl text-[#1C1917] ml-0.5"></i>
                </button>
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-1 text-sm font-medium bg-black/70 text-white rounded-full">
                {lecture.duration}
              </div>
              {lecture.isFree && (
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 text-sm font-bold bg-emerald-500 text-white rounded-full">
                    무료 강의
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${difficultyConfig[lecture.difficulty].bg} ${difficultyConfig[lecture.difficulty].text} ${difficultyConfig[lecture.difficulty].border}`}>
                  {lecture.difficulty}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full border border-[#C4956A]/20">
                  {lecture.categoryLabel}
                </span>
              </div>

              <h1 className="text-xl md:text-2xl font-bold text-[#1C1917] mb-3 leading-snug">
                {lecture.title}
              </h1>
              <p className="text-sm text-[#5C5650] mb-4 leading-relaxed">
                {lecture.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-1.5 mb-5">
                {lecture.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 text-[11px] bg-[#F5F3F0] text-[#6B6560] rounded-md cursor-default hover:bg-[#E8E5E0] transition-colors">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-[#8C8780] mb-8 pb-6 border-b border-[#F0EDE8]">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-[#C4956A]"></i>
                  <strong className="text-[#1C1917]">{lecture.instructor}</strong> 강사
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {lecture.views.toLocaleString()}회 시청
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-thumb-up-line"></i>
                  {lecture.likes.toLocaleString()}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  총 {lecture.duration}
                </span>
              </div>

              {/* Curriculum */}
              <div>
                <h2 className="text-base font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                  <i className="ri-list-check-2 text-[#C4956A]"></i>
                  커리큘럼
                  <span className="text-xs font-normal text-[#8C8780]">
                    ({lecture.curriculum.length}개 챕터 · 총 {lecture.duration})
                  </span>
                </h2>
                <div className="space-y-2">
                  {lecture.curriculum.map((item) => (
                    <div
                      key={item.chapter}
                      className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-[#F0EDE8] hover:border-[#C4956A]/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-[#C4956A]">{item.chapter}</span>
                      </div>
                      <span className="flex-1 text-sm text-[#5C5650] font-medium">{item.title}</span>
                      <span className="flex items-center gap-1 text-xs text-[#8C8780]">
                        <i className="ri-time-line"></i>
                        {item.duration}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-24">
                <div className="bg-white rounded-2xl border border-[#E8E5E0] p-5">
                  <div className="text-center mb-5">
                    <div className="text-2xl font-bold text-[#C4956A] mb-1">
                      {lecture.isFree ? "무료" : "유료"}
                    </div>
                    <p className="text-xs text-[#8C8780]">
                      {lecture.isFree ? "자유롭게 수강하세요" : "수강 신청 후 이용 가능"}
                    </p>
                  </div>

                  <button
                    onClick={() => setIsRegistrationOpen(true)}
                    className="w-full py-3 text-sm font-semibold rounded-xl bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer mb-2.5 flex items-center justify-center gap-2"
                  >
                    <i className="ri-check-line"></i>
                    수강 신청하기
                  </button>

                  <button
                    onClick={() => setIsPreviewOpen(true)}
                    className="w-full py-3 text-sm font-medium rounded-xl border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-eye-line"></i>
                    3분 미리보기
                  </button>

                  {/* Features */}
                  <div className="mt-5 pt-5 border-t border-[#F0EDE8] space-y-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-emerald-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#1C1917]">무제한 수강</p>
                        <p className="text-[10px] text-[#8C8780]">언제 어디서나</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-download-2-line text-emerald-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#1C1917]">PDF 가이드북 제공</p>
                        <p className="text-[10px] text-[#8C8780]">수강 완료 시 다운로드</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-question-answer-line text-emerald-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#1C1917]">질문 답변</p>
                        <p className="text-[10px] text-[#8C8780]">강사 직접 답변</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* PDF Download Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-12"
          >
            <div className="bg-white rounded-2xl border border-[#E8E5E0] p-8 md:p-10">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Left */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#C4956A]/10 flex items-center justify-center">
                      <i className="ri-file-pdf-2-line text-xl text-[#C4956A]"></i>
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-[#1C1917]">
                        핵심 요약 PDF 가이드북
                      </h2>
                      <p className="text-xs text-[#8C8780]">무료 다운로드</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#5C5650] mb-4 leading-relaxed">
                    이 강의의 핵심 내용을 정리한 PDF 가이드북을 무료로 제공합니다.
                    수강 완료 후 확인 가능한 핵심 포인트와 체크리스트가 포함되어 있어
                    실전에서 바로 활용하실 수 있습니다.
                  </p>
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-[#C4956A] text-xs"></i>
                      강의 핵심 내용 요약 정리
                    </li>
                    <li className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-[#C4956A] text-xs"></i>
                      실전 체크리스트 포함
                    </li>
                    <li className="flex items-center gap-2 text-xs text-[#5C5650]">
                      <i className="ri-checkbox-circle-fill text-[#C4956A] text-xs"></i>
                      인쇄 가능한 A4 사이즈
                    </li>
                  </ul>
                </div>

                {/* Right - Form */}
                <div className="flex-1 w-full">
                  <form
                    onSubmit={handleFormSubmit}
                    data-readdy-form
                    className="space-y-3"
                  >
                    <div>
                      <label className="block text-xs font-medium text-[#5C5650] mb-1">
                        이메일 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="example@email.com"
                        className="w-full px-3.5 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-[#FAFAF8] focus:outline-none focus:border-[#C4956A] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#5C5650] mb-1">
                        연락처 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="010-0000-0000"
                        className="w-full px-3.5 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-[#FAFAF8] focus:outline-none focus:border-[#C4956A] transition-colors"
                      />
                    </div>

                    {/* Honeypot */}
                    <div className="form_aux_wrapper">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 text-sm font-semibold rounded-xl bg-[#1C1917] text-white hover:bg-[#3A3530] transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          처리 중...
                        </>
                      ) : (
                        <>
                          <i className="ri-download-2-line"></i>
                          PDF 가이드북 무료 다운로드
                        </>
                      )}
                    </button>

                    {formStatus === "success" && (
                      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                        <p className="text-xs font-medium text-emerald-700 mb-2">
                          신청이 완료되었습니다!
                        </p>
                        <p className="text-[11px] text-emerald-600">
                          입력하신 이메일로 PDF 가이드북 다운로드 링크를 보내드렸습니다.
                        </p>
                      </div>
                    )}

                    {formStatus === "error" && (
                      <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                        <p className="text-xs font-medium text-rose-700">{formError}</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />

      {lecture && (
        <>
          <RegistrationModal
            isOpen={isRegistrationOpen}
            onClose={() => setIsRegistrationOpen(false)}
            courseTitle={lecture.title}
            courseId={lecture.id}
          />
          <PreviewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            title={lecture.title}
            previewContent={lecture.previewContent}
            instructor={lecture.instructor}
            duration={lecture.duration}
            curriculum={lecture.curriculum}
          />
        </>
      )}
    </div>
  );
}